import { useState } from "react";
import { Check, HelpCircle, Loader2, MapPin, Stamp, UtensilsCrossed } from "lucide-react";
import { BonusUnavailable, earnStamp, type EarnStampResult } from "@/data/bonus";
import { useTranslation } from "@/i18n/I18nProvider";
import type { TranslationKey } from "@/i18n/translations";
import type { GeolocationState } from "@/hooks/useGeolocation";
import { BONUS_MAX_ACCURACY_M, BONUS_MIN_ACCOUNT_AGE_HOURS } from "@/lib/bonusFormat";
import { cn } from "@/lib/cn";
import { coordsOf, haversineKm, PROXIMITY_M } from "@/lib/geo";
import { recordVisit } from "@/lib/visits";
import { selectSignedIn, useAuthStore } from "@/services/authStore";
import type { Restaurant } from "@/types/restaurant";

/**
 * "Dine In!" check-in — the primary, user-tapped conversion capture.
 *
 * Appears only when the diner is physically close to a venue they opened
 * in-app: it reads the SAME foreground `useGeolocation` the feed already uses
 * (no new prompt, no background tracking) and shows within `PROXIMITY_M`. A `?`
 * button explains, truthfully, that a check-in records presence and nothing
 * else. On tap it writes a `visits` row (method `check_in`) and offers to leave
 * feedback. If location is off it shows a gentle enable hint instead.
 *
 * A signed-in diner's tap goes through `earn_stamp` (§33c, docs/features/bonus.md
 * Rule 1): the same visit plus a stamp decision, shown as one line under the
 * check-in. Guests and staff keep `record_visit`, and so does everyone before
 * §33. The fix's real GPS accuracy goes to the server either way.
 */

type State =
  | { kind: "idle" }
  | { kind: "recording" }
  | { kind: "done"; visitId: number; stamp: EarnStampResult | null; accuracy: number | null }
  | { kind: "error" };

/** The stamp line for each outcome; `not_participating` and `staff` say nothing. */
const STAMP_KEY: Partial<Record<EarnStampResult["stamp"], TranslationKey>> = {
  earned: "DINE_IN_STAMP_EARNED",
  minted: "DINE_IN_STAMP_MINTED",
  low_accuracy: "DINE_IN_STAMP_LOW_ACCURACY",
  too_new: "DINE_IN_STAMP_TOO_NEW",
  daily_limit: "DINE_IN_STAMP_DAILY",
  already: "DINE_IN_STAMP_ALREADY",
  capped: "DINE_IN_STAMP_CAPPED",
  frozen: "DINE_IN_STAMP_FROZEN",
  too_fast: "DINE_IN_STAMP_TOO_FAST",
};

const lineButton =
  "shrink-0 rounded-lg border border-white/15 light:border-black/15 px-3 py-1.5 text-xs font-semibold text-zinc-100 light:text-zinc-900 transition hover:border-white/40 light:hover:border-black/40";

export function DineInButton({
  restaurant,
  geo,
  onLeaveFeedback,
  onOpenBonus,
}: {
  restaurant: Restaurant;
  geo: GeolocationState;
  onLeaveFeedback: (visitId: number) => void;
  /** Opens the sidebar's stamp card (the stamp line's button). */
  onOpenBonus?: () => void;
}) {
  const { t } = useTranslation();
  const [state, setState] = useState<State>({ kind: "idle" });
  const [showInfo, setShowInfo] = useState(false);
  const authStatus = useAuthStore((s) => s.status);
  const signedIn = useAuthStore(selectSignedIn);
  const isStaff = useAuthStore((s) => s.profile) !== null;

  const venueCoords = coordsOf(restaurant);
  // No coordinates on the venue → we can't verify presence, so no button.
  if (!venueCoords) return null;

  const distanceM = geo.coords ? haversineKm(geo.coords, venueCoords) * 1000 : null;
  const near = distanceM != null && distanceM <= PROXIMITY_M;

  // Location off/denied/unavailable → a gentle enable hint (never auto-prompt).
  if (!geo.coords) {
    if (geo.status === "idle") {
      return (
        <button
          type="button"
          onClick={geo.request}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/[0.12] light:border-black/10 bg-zinc-900/50 light:bg-white/60 px-4 py-2.5 text-sm font-medium text-zinc-300 light:text-zinc-700 transition hover:border-white/30 light:hover:border-black/30 hover:text-zinc-100 light:hover:text-zinc-900"
        >
          <MapPin className="h-4 w-4 text-dim light:text-zinc-600" aria-hidden />
          {t("DINE_IN_ENABLE_LOCATION")}
        </button>
      );
    }
    return null;
  }

  // Have a fix but too far → the button simply doesn't apply here.
  if (!near) return null;

  async function checkIn() {
    if (!geo.coords) return;
    const coords = geo.coords;
    const accuracy = geo.accuracy;
    setState({ kind: "recording" });
    // The fix goes to the server for its own 75 m re-check (§31c) and is
    // discarded there. A re-tap the same day reuses that day's visit, so a
    // retry after `low_accuracy` can still earn the stamp.
    if (signedIn && !isStaff) {
      try {
        const res = await earnStamp(restaurant.id, coords, accuracy);
        setState({ kind: "done", visitId: res.visit_id, stamp: res, accuracy });
        return;
      } catch (err) {
        if (!(err instanceof BonusUnavailable)) {
          setState({ kind: "error" });
          return;
        }
        // Before §33: the plain visit below.
      }
    }
    const visit = await recordVisit(restaurant.id, "check_in", coords, accuracy);
    if (visit) setState({ kind: "done", visitId: visit.id, stamp: null, accuracy });
    else setState({ kind: "error" });
  }

  if (state.kind === "done") {
    const stamp = state.stamp;
    const stampKey = stamp ? STAMP_KEY[stamp.stamp] : undefined;
    const good = stamp?.stamp === "earned" || stamp?.stamp === "minted";
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3 rounded-xl border border-emerald-400/25 bg-emerald-400/[0.06] px-4 py-3">
          <span className="inline-flex items-center gap-2 text-sm font-medium text-emerald-300 light:text-emerald-700">
            <Check className="h-4 w-4" aria-hidden />
            {t("DINE_IN_DONE")}
          </span>
          <button
            type="button"
            onClick={() => onLeaveFeedback(state.visitId)}
            className="shrink-0 rounded-lg bg-paper light:bg-ink px-3 py-1.5 text-xs font-semibold text-ink light:text-paper transition hover:bg-white light:hover:bg-zinc-800"
          >
            {t("DINE_IN_LEAVE_FEEDBACK")}
          </button>
        </div>
        {stamp && stampKey && (
          <div
            role="status"
            className={cn(
              "flex items-center justify-between gap-3 rounded-xl border px-4 py-2.5",
              good
                ? "border-pulse/30 bg-pulse/[0.07]"
                : "border-white/[0.12] light:border-black/10 bg-zinc-900/50 light:bg-white/60",
            )}
          >
            <span
              className={cn(
                "inline-flex min-w-0 items-start gap-2 text-xs leading-relaxed",
                good ? "font-medium text-zinc-100 light:text-zinc-900" : "text-zinc-300 light:text-zinc-700",
              )}
            >
              <Stamp
                className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", good ? "text-pulse" : "text-dim light:text-zinc-600")}
                aria-hidden
              />
              {t(stampKey, {
                n: stamp.progress ?? 0,
                total: stamp.required ?? 0,
                // The accuracy sent with this tap, the one the server judged.
                m: Math.round(state.accuracy ?? 0),
                max: BONUS_MAX_ACCURACY_M,
                hours: BONUS_MIN_ACCOUNT_AGE_HOURS,
              })}
            </span>
            {stamp.stamp === "low_accuracy" ? (
              <button type="button" onClick={() => void checkIn()} className={lineButton}>
                {t("RETRY")}
              </button>
            ) : (
              onOpenBonus && (
                <button type="button" onClick={onOpenBonus} className={lineButton}>
                  {t("DINE_IN_OPEN_CARD")}
                </button>
              )
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative space-y-2">
      <div className="flex items-stretch gap-2">
        <button
          type="button"
          onClick={() => void checkIn()}
          // Wait for auth to settle so a signed-in tap is never recorded as a guest's.
          disabled={state.kind === "recording" || authStatus === "initializing"}
          className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-pulse px-4 py-3 text-sm font-semibold text-white shadow-[0_6px_22px_-8px_rgba(244,63,94,0.6)] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {state.kind === "recording" ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <UtensilsCrossed className="h-4 w-4" aria-hidden />
          )}
          {t("DINE_IN_CTA")}
        </button>
        <button
          type="button"
          aria-label={t("DINE_IN_INFO_LABEL")}
          aria-expanded={showInfo}
          onClick={() => setShowInfo((v) => !v)}
          className="grid w-11 shrink-0 place-items-center rounded-xl border border-white/[0.12] light:border-black/10 bg-zinc-900/60 light:bg-white/70 text-zinc-400 light:text-zinc-600 transition hover:border-white/30 light:hover:border-black/30 hover:text-zinc-100 light:hover:text-zinc-900"
        >
          <HelpCircle className="h-4 w-4" aria-hidden />
        </button>
      </div>

      {showInfo && (
        <p className="rounded-xl border border-white/[0.12] light:border-black/10 bg-zinc-900/80 light:bg-white/80 px-3.5 py-3 text-xs leading-relaxed text-zinc-300 light:text-zinc-700">
          {t("DINE_IN_EXPLAINER")}
        </p>
      )}

      {state.kind === "error" && (
        <p role="alert" className="px-1 text-xs text-rose-300">
          {t("DINE_IN_ERROR")}
        </p>
      )}
    </div>
  );
}
